'use client'

import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import { useUser, useClerk, useSession } from '@clerk/nextjs'
import Image from 'next/image'
import Link from 'next/link'
import { useSettings } from '@/providers/SettingsProvider'
import { DropdownUserLanguages } from './DropdownUserLanguages'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useRouter } from 'next/navigation'
import { toast } from 'sonner'
import { fetchStatisticsData, fetchUserMembershis } from '@/lib/action'
import { setMembership, setPermission } from '@/lib/slices/customerSlice'
import { ScreenLoader } from '../../loaders'

export default function UserProfile() {
    const { user, isLoaded: isUserLoaded } = useUser();
    const { session, isLoaded: isSessionLoaded } = useSession();
    const router = useRouter();
    const { signOut } = useClerk()
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const { settings, storeSettings } = useSettings();
    const itemUserRef = useRef<HTMLDivElement | null>(null);
    const dispatch = useDispatch();
    const { membershiperror, permissionerror } = useSelector((state: any) => state.customer)
    const intl = useIntl();
    const dropdownRef = useRef<HTMLDivElement | null>(null);

    const toggleDropdown = () => setIsDropdownOpen(prev => !prev)

    const handleThemeMode = (event: ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();
        const newThemeMode = event.target.checked ? 'dark' : 'light';
        storeSettings({
            themeMode: newThemeMode
        });
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (
            dropdownRef.current &&
            !dropdownRef.current.contains(event.target as Node)
        ) {
            setIsDropdownOpen(false);
        }
    };


    useEffect(() => {
        if (isDropdownOpen) {
            window.addEventListener('click', handleClickOutside);
        } else {
            window.removeEventListener('click', handleClickOutside);
        }

        return () => {
            window.removeEventListener('click', handleClickOutside);
        };
    }, [isDropdownOpen]);

    useEffect(() => {
        if (!isUserLoaded || !isSessionLoaded) return;
        if (!user) {
            toast.error(intl.formatMessage({ id: 'USER_ID_MISSING' }));
            router.push('/sign-in');
            return;
        }
        if (!session) {
            toast.error(intl.formatMessage({ id: 'SESSION_ID_MISSING' }));
            router.push('/sign-in');
            return;
        }
        const fetchMembership = async () => {
            try {
                const resMembership = await fetchUserMembershis(user.id, session.id);
                dispatch(setMembership({ membership: resMembership }));
                if (membershiperror) toast.error(membershiperror || intl.formatMessage({ id: 'SOMETHING_WENT_WRONG' }));
            } catch (err: any) {
                toast.error(err?.message || err?.msg || err?.err || err?.error || intl.formatMessage({ id: 'MEMBERSHIP.ERROR' }));
            }
        };

        const fetchStatistics = async () => {
            try {
                const resStats = await fetchStatisticsData(user.id, session.id);
                dispatch(setPermission({ permission: resStats }));
                if (permissionerror) toast.error(permissionerror || intl.formatMessage({ id: 'SOMETHING_WENT_WRONG' }));
            } catch (err: any) {
                toast.error(err?.message || err?.msg || err?.err || err?.error || intl.formatMessage({ id: 'STATISTICS.ERROR' }));
            }
        };
        fetchMembership();
        fetchStatistics();

        const intervalId = setInterval(() => {
            fetchMembership();
            fetchStatistics();
        }, 1800000);
        return () => clearInterval(intervalId);
    }, []);

    if (!isUserLoaded || !isSessionLoaded) {
        return <ScreenLoader />;
    }
    return (
        <div className="flex items-center gap-2 lg:gap-3.5" ref={dropdownRef}>

            <div className="menu">
                <div
                    className={`menu-item menu-item-dropdown ${isDropdownOpen ? 'open' : ''}`}
                    onClick={toggleDropdown}
                    style={{ position: 'relative' }}
                >
                    {/* Avatar Toggle */}
                    <div className="menu-toggle btn btn-icon rounded-full">
                        <Image
                            src={user?.hasImage ? user?.imageUrl ?? "" : "/assets/media/app/Asset-16@4x.png"}
                            alt="User Avatar"
                            width={36}
                            height={36}
                            className="size-9 rounded-full border-2 border-success shrink-0 object-contain"
                        />
                    </div>

                    {/* Dropdown Menu */}
                    <div
                        className={`menu-dropdown menu-default light:border-gray-300 w-[280px] absolute ${isDropdownOpen ? 'block' : 'hidden'}`}
                        style={{
                            top: '48px',
                            right: "-23px",
                        }}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-5 py-1.5 gap-1.5">
                            <div className="flex items-center gap-2">
                                <Image
                                    src={user?.hasImage ? user?.imageUrl ?? "" : "/assets/media/app/Asset-16@4x.png"}
                                    alt="User Avatar"
                                    width={36}
                                    height={36}
                                    className="size-9 rounded-full border-2 border-success object-contain"
                                />
                                <div className="flex flex-col gap-1.5">
                                    <span className="text-sm text-gray-800 font-semibold leading-none">
                                        {user?.fullName || ""}
                                    </span>
                                    <span className="text-xs text-gray-600 font-medium leading-none">
                                        {user?.primaryEmailAddress?.emailAddress}
                                    </span>
                                </div>
                            </div>
                            {/* <span className="badge badge-xs badge-primary badge-outline">Pro</span> */}
                        </div>

                        <div className="menu-separator" />

                        {/* Menu Items */}
                        <div className="flex flex-col">
                            <div className="menu-item">
                                <Link className="menu-link" href="/user" prefetch={false}>
                                    <span className="menu-icon"><i className="ki-filled ki-badge"></i></span>
                                    <span className="menu-title"><FormattedMessage id="USER.MENU.PUBLIC_PROFILE" /></span>
                                </Link>
                            </div>
                            <DropdownUserLanguages menuItemRef={itemUserRef} />
                            <div className="menu-separator"></div>
                        </div>
                        <div className="flex flex-col">
                            {/* Dark Mode Toggle */}
                            <div className="menu-item mb-0.5">
                                <div className="menu-link">
                                    <span className="menu-icon"><i className="ki-filled ki-moon"></i></span>
                                    <span className="menu-title"> <FormattedMessage id="USER.MENU.DARK_MODE" /></span>
                                    <label className="switch switch-sm">
                                        <input
                                            name="theme"
                                            type="checkbox"
                                            checked={settings.themeMode === 'dark'}
                                            onChange={handleThemeMode}
                                            value="1"
                                        />
                                    </label>
                                </div>
                            </div>

                            {/* Sign Out */}
                            <div className="menu-item px-4 py-1.5">
                                <button
                                    onClick={() => signOut()}
                                    className="btn btn-sm btn-light justify-center w-full"
                                >
                                    <FormattedMessage id="USER.MENU.LOGOUT" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
